import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
  Button,
  TextField,
  MuiThemeProvider,
  createMuiTheme,
  FormControlLabel,
  Checkbox,
  MenuItem,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { useHistory, useParams, useLocation } from "react-router-dom";
import {
  deleteUser,
  getUserById,
  getNextSocioNumberPreview,
  postUser,
  updateUser,
  resetUserPassword,
} from "../../../../api/user";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { getUserTypes } from "../../../../api/userType";
import { getPaises } from "../../../../api/pais";
import { getLanguages } from "../../../../api/language"; // Asumiendo que existe esta API
import { Autocomplete } from "@material-ui/lab";
import Select from "react-select";

// Create theme for delete button (red)
const theme = createMuiTheme({
  palette: {
    secondary: {
      main: "#F64E60",
    },
  },
});

// Tipos de vía españoles más comunes
const TIPOS_VIA = [
  { value: "CALLE", label: "Calle" },
  { value: "AVENIDA", label: "Avenida" },
  { value: "PLAZA", label: "Plaza" },
  { value: "PASEO", label: "Paseo" },
  { value: "CARRERA", label: "Carrera" },
  { value: "RONDA", label: "Ronda" },
  { value: "TRAVESIA", label: "Travesía" },
  { value: "CAMINO", label: "Camino" },
  { value: "PASAJE", label: "Pasaje" },
  { value: "BULEVAR", label: "Bulevar" },
  { value: "GLORIETA", label: "Glorieta" },
  { value: "ALAMEDA", label: "Alameda" },
  { value: "CUESTA", label: "Cuesta" },
  { value: "RAMBLA", label: "Rambla" },
  { value: "VIA", label: "Vía" },
];

function userHasAssignedNSocio(user) {
  const n = user?.nAso;
  if (typeof n === "number" && Number.isFinite(n) && n > 0) return true;
  if (n != null && n !== "") {
    const parsed = parseInt(String(n).trim(), 10);
    if (Number.isFinite(parsed) && parsed > 0) return true;
  }
  return false;
}

/** Valor mostrado en el campo Nº socio (solo lectura): guardado, previsión API o vacío. */
function formatNSocioReadOnlyValue(user, previewNum, previewLoading) {
  if (userHasAssignedNSocio(user)) {
    const n = user.nAso;
    if (typeof n === "number" && Number.isFinite(n)) return String(n);
    const parsed = parseInt(String(n).trim(), 10);
    return Number.isFinite(parsed) ? String(parsed) : "";
  }
  if (user?.asociado) {
    if (previewLoading) return "…";
    if (previewNum != null && Number.isFinite(Number(previewNum)))
      return String(previewNum);
    return "";
  }
  return "";
}

function getEmptyUser() {
  return {
    nickname: "",
    fullName: "",
    apellido1: "",
    apellido2: "",
    email: "",
    type: "",
    nAso: "",
    asociado: false,
    jd: false,
    mailing: false,
    seram: false,
    role: "user",
    password: "",
    repeatPassword: "",
    active: true,

    // Nuevos campos obligatorios para socios
    dni: "",
    telefono: "",
    fechaAlta: "",
    fechaBaja: "",
    afiliacion: "",
    ultimoPago: "",
    fechaCaducidadEstudiante: "",
    comentarios: "",
    idioma: "",
    fechaNacimiento: "",

    // Domicilio
    via: "",
    direccion: "",
    numero: "",
    piso: "",
    puerta: "",
    poblacion: "",
    provincia: "",
    pais: "",
    cp: "",

    // Datos laborales (no obligatorios)
    centro: "",
    departamento: "",
    funciones: "",
    titulo: "",
    direccionL: "",
    cpL: "",

    // Campos bancarios
    ultimoPagoB: "",
    entidadB: "",
    direccionB: "",
    provinciaB: "",
    poblacionB: "",
    cpB: "",
    titular: "",
    nif: "",
    iban: "",
  };
}

export default function EditUsersPage() {
  const [user, setUser] = useState(getEmptyUser());
  const [paises, setPaises] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [newPassword, setNewPassword] = useState({
    password: null,
    repeatPassword: null,
  });
  const [changePassword, setChangePassword] = useState(false);
  const [selectedType, setSelectedType] = useState({});
  const [selectedPais, setSelectedPais] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [types, setTypes] = useState([]);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [nSocioPreview, setNSocioPreview] = useState(null);
  const [nSocioPreviewLoading, setNSocioPreviewLoading] = useState(false);
  const userId = useParams().id;
  const history = useHistory();
  const location = useLocation();
  const fromSocios =
    location.state?.fromSocios ||
    (typeof window !== "undefined" &&
      localStorage.getItem("editUserFromSocios") === "true");

  // Limpiar el flag cuando salimos de la página
  useEffect(() => {
    return () => {
      if (fromSocios) {
        localStorage.removeItem("editUserFromSocios");
      }
    };
  }, [fromSocios]);

  const [fieldsLocked, setFieldsLocked] = useState({
    cp: true,
    poblacion: true,
    provincia: true,
  });

  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  // Función para validar IBAN español
  const validateIBAN = (iban) => {
    if (!iban) return true; // Campo opcional
    // Formato básico IBAN español: ES + 2 dígitos + 20 dígitos más
    const ibanRegex = /^ES\d{22}$/;
    return ibanRegex.test(iban.replace(/\s/g, ""));
  };

  // Función para formatear IBAN
  const formatIBAN = (value) => {
    if (!value) return "";
    // Eliminar espacios y convertir a mayúsculas
    const cleanValue = value.replace(/\s/g, "").toUpperCase();
    // Añadir espacios cada 4 caracteres
    return cleanValue.replace(/(.{4})/g, "$1 ").trim();
  };

  useEffect(() => {
    if (!user?.asociado || userHasAssignedNSocio(user)) {
      setNSocioPreview(null);
      setNSocioPreviewLoading(false);
      return;
    }
    let cancelled = false;
    setNSocioPreviewLoading(true);
    getNextSocioNumberPreview()
      .then((res) => {
        if (!cancelled && res.status === 200 && res.data?.nextNAso != null) {
          setNSocioPreview(res.data.nextNAso);
        }
      })
      .catch(() => {
        if (!cancelled) setNSocioPreview(null);
      })
      .finally(() => {
        if (!cancelled) setNSocioPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.asociado, user?.nAso]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const resTypes = await getUserTypes();
        if (resTypes.status === 200) {
          setTypes(resTypes.data);
        }

        // Cargar países
        const resPaises = await getPaises();
        if (resPaises.status === 200) {
          setPaises(resPaises.data);
        }

        // Cargar idiomas
        try {
          const resLanguages = await getLanguages();
          if (resLanguages.status === 200) {
            // Filtrar idiomas con "Unknown" o valores inválidos
            const validLanguages = resLanguages.data.filter(
              (lang) =>
                lang &&
                lang.fullName &&
                lang.fullName.trim() !== "" &&
                lang.fullName.toLowerCase() !== "unknown" &&
                lang.isocode &&
                lang.isocode.trim() !== "",
            );
            setLanguages(validLanguages);
          }
        } catch (error) {
          console.warn("No se pudieron cargar los idiomas:", error);
        }

        if (!userId) {
          disableLoadingData();
          return;
        }

        getUserById(userId)
          .then((res) => {
            if (res.status === 200) {
              const userData = res.data;
              delete userData.password;
              setUser(userData);

              // Configurar selecciones
              setSelectedType(
                resTypes.data.find((x) => x._id === userData.type),
              );
              const selectedCountry = resPaises.data.find(
                (x) => x._id === userData.pais,
              );
              setSelectedPais(selectedCountry);

              // Desbloquear campos según los datos existentes
              if (selectedCountry) {
                setFieldsLocked((prev) => ({ ...prev, cp: false }));
                if (userData.cp) {
                  setFieldsLocked((prev) => ({
                    ...prev,
                    poblacion: false,
                    provincia: false,
                  }));
                }
              }

              disableLoadingData();
            }
          })
          .catch((error) => {
            alertError({
              error: error,
              customMessage: "No se pudo obtener el usuario.",
            });
            history.push("/users");
          });
      } catch (error) {
        alertError({
          error: error,
          customMessage: "Error al cargar los datos.",
        });
      }
    };
    fetchData();
  }, [userId, disableLoadingData, history]);

  // Efecto separado para establecer el idioma cuando tanto el usuario como los idiomas estén cargados
  useEffect(() => {
    if (user.idioma && languages.length > 0) {
      // Manejar diferentes formatos de ID (string directo o objeto con $oid)
      const idiomaId =
        typeof user.idioma === "object" && user.idioma.$oid
          ? user.idioma.$oid
          : user.idioma;

      // Convertir a string para comparación
      const idiomaIdString = String(idiomaId);

      const foundLanguage = languages.find((lang) => {
        // Comparar tanto _id como string
        const langIdString = String(lang._id);
        return langIdString === idiomaIdString;
      });

      if (foundLanguage) {
        setSelectedLanguage(foundLanguage);
      } else {
        console.warn(
          "Idioma no encontrado en la lista:",
          idiomaIdString,
          "Idiomas disponibles:",
          languages.map((l) => ({ id: l._id, name: l.fullName })),
        );
      }
    }
  }, [user.idioma, languages]);

  // Función para obtener población desde Google Maps API
  const getPopulationFromCP = async (cp, paisCode, clearFields = false) => {
    if (!cp || cp.length < 4 || !paisCode) {
      if (clearFields) {
        setUser((prev) => ({
          ...prev,
          poblacion: "",
          provincia: "",
        }));
        setFieldsLocked((prev) => ({
          ...prev,
          poblacion: true,
          provincia: true,
        }));
      }
      return;
    }

    setIsLoadingLocation(true);
    try {
      const API_GOOGLE = process.env.REACT_APP_API_GOOGLE;
      const addressQuery = `${cp}, ${paisCode}`;
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${addressQuery}&key=${API_GOOGLE}`,
      );
      const data = await response.json();

      if (data.status === "OK" && data.results.length > 0) {
        const result = data.results[0];
        const components = result.address_components;
        console.log(result);

        const localityComponent = components.find(
          (comp) =>
            comp.types.includes("locality") ||
            comp.types.includes("administrative_area_level_2"),
        );

        const provinceComponent = components.find((comp) =>
          comp.types.includes("administrative_area_level_1"),
        );

        if (localityComponent) {
          setUser((prev) => {
            // Usar short_name para provincia para evitar nombres largos como "Cataluña"
            // Si no existe short_name, usar long_name pero limpiar si contiene "Cataluña" o similar
            let provinciaName = provinceComponent
              ? provinceComponent.short_name || provinceComponent.long_name
              : prev.provincia;

            // Limpiar nombres de comunidad autónoma que puedan aparecer
            if (provinciaName && typeof provinciaName === "string") {
              // Si contiene "Cataluña", intentar obtener solo el nombre de la provincia
              if (
                provinciaName.toLowerCase().includes("cataluña") ||
                provinciaName.toLowerCase().includes("catalonia")
              ) {
                // Usar el código postal para determinar la provincia catalana
                // Si el CP empieza por 08, es Barcelona; 17 es Girona; 25 es Lleida; 43 es Tarragona
                if (cp && cp.length >= 2) {
                  const cpPrefix = cp.substring(0, 2);
                  const provinciasCatalanas = {
                    "08": "Barcelona",
                    "17": "Girona",
                    "25": "Lleida",
                    "43": "Tarragona",
                  };
                  if (provinciasCatalanas[cpPrefix]) {
                    provinciaName = provinciasCatalanas[cpPrefix];
                  }
                }
              }
            }

            return {
              ...prev,
              poblacion: localityComponent.long_name,
              provincia: provinciaName || prev.provincia,
            };
          });

          // Desbloquear campos después de obtener los datos
          setFieldsLocked((prev) => ({
            ...prev,
            poblacion: false,
            provincia: false,
          }));
        }
      } else {
        // Si no se encuentran resultados, limpiar y bloquear campos
        setUser((prev) => ({
          ...prev,
          poblacion: "",
          provincia: "",
        }));
        setFieldsLocked((prev) => ({
          ...prev,
          poblacion: true,
          provincia: true,
        }));
      }
    } catch (error) {
      console.error("Error al obtener la población:", error);
    } finally {
      setIsLoadingLocation(false);
    }
  };

  function saveUser() {
    // Validaciones para campos obligatorios
    if (!user.fullName || !user.apellido1 || !user.email) {
      alertError({
        error: null,
        customMessage: "Los campos nombre, apellido y email son obligatorios.",
      });
      return;
    }

    // Validaciones adicionales para socios
    if (user.asociado) {
      if (
        !user.dni ||
        !user.telefono ||
        !user.fechaAlta ||
        !user.fechaNacimiento
      ) {
        alertError({
          error: null,
          customMessage:
            "Para socios son obligatorios: NIF, teléfono, fecha de alta y fecha de nacimiento.",
        });
        return;
      }
    }

    // Validación de IBAN si se ha introducido
    if (user.iban && !validateIBAN(user.iban)) {
      alertError({
        error: null,
        customMessage:
          "El formato del IBAN no es válido. Debe seguir el formato: ES + 22 dígitos.",
      });
      return;
    }

    let saveUser = { ...user };

    // Gestión de contraseñas
    if (!userId || changePassword) {
      if (!newPassword.password || !newPassword.repeatPassword) {
        alertError({
          error: null,
          customMessage: "Por favor, introduce la contraseña.",
        });
        return;
      }
      if (newPassword.password !== newPassword.repeatPassword) {
        alertError({
          error: null,
          customMessage: "Las contraseñas no coinciden.",
        });
        return;
      }
      saveUser = { ...saveUser, password: newPassword.password };
    }

    // Asignar IDs de selecciones
    if (selectedType) saveUser.type = selectedType._id;
    if (selectedPais) saveUser.pais = selectedPais._id;
    if (selectedLanguage) saveUser.idioma = selectedLanguage._id;

    // Limpiar campos ObjectId vacíos (convertir cadenas vacías a null/undefined)
    // Esto evita errores de validación de Mongoose al intentar convertir "" a ObjectId
    if (saveUser.idioma === "" || saveUser.idioma === null) {
      delete saveUser.idioma;
    }
    if (saveUser.type === "" || saveUser.type === null) {
      delete saveUser.type;
    }
    if (saveUser.pais === "" || saveUser.pais === null) {
      delete saveUser.pais;
    }

    // Limpiar formato del IBAN para guardar
    if (saveUser.iban) {
      saveUser.iban = saveUser.iban.replace(/\s/g, "");
    }

    if (!userId) {
      postUser(saveUser)
        .then((res) => {
          if (res.status === 201) {
            alertSuccess({
              title: "¡Guardado!",
              customMessage: "Usuario creado correctamente.",
            });
            history.push(fromSocios ? "/socios" : "/users");
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "No se pudo guardar el usuario.",
          });
        });
    } else {
      updateUser(userId, saveUser)
        .then((res) => {
          if (res.status === 200) {
            alertSuccess({
              title: "¡Guardado!",
              customMessage: "Cambios guardados correctamente.",
            });
            history.push(fromSocios ? "/socios" : "/users");
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "No se pudieron guardar los cambios.",
          });
        });
    }
  }

  const renderStickyButtons = ({
    onBack,
    onSave,
    onDelete,
    setObj,
    obj,
    showDelete = false,
  }) => {
    return (
      <div
        className="sticky-top bg-white border-top shadow-sm"
        style={{
          zIndex: 1000,
          paddingLeft: "15px",
          paddingRight: "15px",
        }}
      >
        <div className="container-fluid">
          <div className="row py-3">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                {/* Botón Back */}
                <div className="d-flex gap-2">
                  <Button
                    onClick={onBack}
                    variant="outlined"
                    size="medium"
                    className="text-nowrap"
                    startIcon={<ArrowBackIcon />}
                  >
                    Back
                  </Button>
                </div>

                {/* Botones Save y Delete */}
                <div className="d-flex gap-2">
                  <Button
                    onClick={onSave}
                    variant="outlined"
                    color="primary"
                    style={{ marginRight: "20px" }}
                  >
                    Save
                  </Button>

                  {showDelete && (
                    <MuiThemeProvider theme={theme}>
                      <Button
                        onClick={onDelete}
                        variant="outlined"
                        color="secondary"
                        size="medium"
                        className="text-nowrap"
                      >
                        Delete
                      </Button>
                    </MuiThemeProvider>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleChange = (element) => (event) => {
    const value = event.target.value;

    // Formateo especial para IBAN
    if (element === "iban") {
      const formattedValue = formatIBAN(value);
      setUser({ ...user, [element]: formattedValue });
      return;
    }

    setUser({ ...user, [element]: value });

    // Si cambia el CP y hay país seleccionado, buscar población
    if (element === "cp" && selectedPais && value.length >= 4) {
      getPopulationFromCP(value, selectedPais.code);
    }
  };

  const handleDateChange = (element) => (event) => {
    const value = event.target.value;
    setUser({ ...user, [element]: value ? new Date(value).toISOString() : "" });
  };

  const htmlBanco = () => {
    return (
      <>
        <div style={{ marginTop: "20px", marginBottom: "20px" }}>
          <h4>Domiciliación Bancaria</h4>
          <hr />
        </div>

        <TextField
          id="ultimoPagoB"
          label="Último pago bancario"
          type="date"
          value={
            user.ultimoPagoB
              ? new Date(user.ultimoPagoB).toISOString().split("T")[0]
              : ""
          }
          onChange={handleDateChange("ultimoPagoB")}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          variant="outlined"
          fullWidth
        />

        <TextField
          id="entidadB"
          label="Entidad bancaria"
          value={user.entidadB || ""}
          onChange={handleChange("entidadB")}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          variant="outlined"
          fullWidth
        />

        <TextField
          id="direccionB"
          label="Dirección de la entidad"
          value={user.direccionB || ""}
          onChange={handleChange("direccionB")}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          variant="outlined"
          fullWidth
        />

        <TextField
          id="provinciaB"
          label="Provincia de la entidad"
          value={user.provinciaB || ""}
          onChange={handleChange("provinciaB")}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          variant="outlined"
          fullWidth
        />

        <TextField
          id="poblacionB"
          label="Población de la entidad"
          value={user.poblacionB || ""}
          onChange={handleChange("poblacionB")}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          variant="outlined"
          fullWidth
        />

        <TextField
          id="cpB"
          label="CP de la entidad"
          value={user.cpB || ""}
          onChange={handleChange("cpB")}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          variant="outlined"
          fullWidth
        />

        <div style={{ marginTop: "20px", marginBottom: "10px" }}>
          <h5>Datos del Titular</h5>
          <hr style={{ margin: "10px 0" }} />
        </div>

        <TextField
          id="titular"
          label="Titular de la cuenta"
          value={user.titular || ""}
          onChange={handleChange("titular")}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          variant="outlined"
          fullWidth
        />

        <TextField
          id="nif"
          label="NIF del titular"
          value={user.nif || ""}
          onChange={handleChange("nif")}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          variant="outlined"
          fullWidth
          helperText="NIF del titular de la cuenta bancaria"
        />

        <TextField
          id="iban"
          label="IBAN"
          value={user.iban || ""}
          onChange={handleChange("iban")}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          variant="outlined"
          fullWidth
          placeholder="ES00 0000 0000 0000 0000 0000"
          helperText={
            user.iban && !validateIBAN(user.iban)
              ? "Formato IBAN incorrecto"
              : "Formato: ES + 22 dígitos"
          }
          error={user.iban && !validateIBAN(user.iban)}
        />
      </>
    );
  };

  const htmlSocios = () => {
    return (
      <>
        <div style={{ marginTop: "20px", marginBottom: "20px" }}>
          <h4>Datos del Socio</h4>
          <hr />
        </div>

        {/* Campos específicos de socio (DNI, teléfono, fecha nacimiento, idioma y domicilio están arriba para todos los usuarios) */}
        <TextField
          id="fechaAlta"
          label="Fecha de alta"
          type="date"
          value={
            user.fechaAlta
              ? new Date(user.fechaAlta).toISOString().split("T")[0]
              : ""
          }
          onChange={handleDateChange("fechaAlta")}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          variant="outlined"
          required={user.asociado}
          fullWidth
        />

        <TextField
          id="fechaBaja"
          label="Fecha de baja"
          type="date"
          value={
            user.fechaBaja
              ? new Date(user.fechaBaja).toISOString().split("T")[0]
              : ""
          }
          onChange={handleDateChange("fechaBaja")}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          variant="outlined"
          fullWidth
        />

        <TextField
          id="afiliacion"
          label="Fecha de afiliación"
          type="date"
          value={
            user.afiliacion
              ? new Date(user.afiliacion).toISOString().split("T")[0]
              : ""
          }
          onChange={handleDateChange("afiliacion")}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          variant="outlined"
          fullWidth
        />

        <TextField
          id="ultimoPago"
          label="Último pago"
          type="date"
          value={
            user.ultimoPago
              ? new Date(user.ultimoPago).toISOString().split("T")[0]
              : ""
          }
          onChange={handleDateChange("ultimoPago")}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          variant="outlined"
          fullWidth
        />

        {selectedType &&
          selectedType.titulo &&
          selectedType.titulo.toLowerCase().includes("estudiante") && (
            <TextField
              id="fechaCaducidadEstudiante"
              label="Fecha de caducidad de categoría Estudiante"
              type="date"
              value={
                user.fechaCaducidadEstudiante
                  ? new Date(user.fechaCaducidadEstudiante)
                      .toISOString()
                      .split("T")[0]
                  : ""
              }
              onChange={handleDateChange("fechaCaducidadEstudiante")}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              fullWidth
              helperText="A partir de esta fecha, el socio pasará a categoría Profesional"
            />
          )}

        <TextField
          id="comentarios"
          label="Comentarios (anotaciones internas)"
          value={user.comentarios || ""}
          onChange={handleChange("comentarios")}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          variant="outlined"
          fullWidth
          multiline
          rows={4}
          helperText="Anotaciones internas sobre el socio"
        />

        <div style={{ marginTop: "20px", marginBottom: "20px" }}>
          <h5>Datos Laborales (Opcionales)</h5>
          <hr />
        </div>

        <TextField
          id="centro"
          label="Centro de trabajo"
          value={user.centro || ""}
          onChange={handleChange("centro")}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          variant="outlined"
          fullWidth
        />

        <TextField
          id="departamento"
          label="Departamento"
          value={user.departamento || ""}
          onChange={handleChange("departamento")}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          variant="outlined"
          fullWidth
        />

        <TextField
          id="funciones"
          label="Funciones / Especialidad"
          value={user.funciones || ""}
          onChange={handleChange("funciones")}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          variant="outlined"
          fullWidth
        />

        <TextField
          id="titulo"
          label="Título Oficial"
          value={user.titulo || ""}
          onChange={handleChange("titulo")}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          variant="outlined"
          fullWidth
        />

        <TextField
          id="direccionL"
          label="Dirección laboral"
          value={user.direccionL || ""}
          onChange={handleChange("direccionL")}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          variant="outlined"
          fullWidth
        />

        <TextField
          id="cpL"
          label="CP laboral"
          value={user.cpL || ""}
          onChange={handleChange("cpL")}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          variant="outlined"
          fullWidth
        />

        {/* Incluir campos bancarios para socios */}
        {htmlBanco()}
      </>
    );
  };

  if (isLoadingData) return <ContentSkeleton />;
  else
    return (
      <>
        <Card>
          {renderStickyButtons({
            onBack: () => history.push(fromSocios ? "/socios" : "/users"),
            onSave: saveUser,
            onDelete: () => setOpenConfirmDialog(true),
            showDelete: !!userId,
            setObj: setUser,
            obj: user,
          })}
          <CardHeader title="Editar usuario"></CardHeader>
          <CardBody>
            {/* Campos básicos obligatorios */}
            <TextField
              id="fullName"
              label="Nombre"
              value={user.fullName || ""}
              onChange={handleChange("fullName")}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              required
              fullWidth
            />

            <TextField
              id="apellido1"
              label="Primer Apellido"
              value={user.apellido1 || ""}
              onChange={handleChange("apellido1")}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              required
              fullWidth
            />

            <TextField
              id="apellido2"
              label="Segundo Apellido"
              value={user.apellido2 || ""}
              onChange={handleChange("apellido2")}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              fullWidth
            />

            <TextField
              id="email"
              label="Email"
              type="email"
              value={user.email || ""}
              onChange={handleChange("email")}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              required
              fullWidth
            />

            <TextField
              id="nickname"
              label="Nombre de usuario"
              value={user.nickname || ""}
              onChange={handleChange("nickname")}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              fullWidth
            />

            <TextField
              id="nAso"
              label="Nº Socio"
              value={formatNSocioReadOnlyValue(
                user,
                nSocioPreview,
                nSocioPreviewLoading,
              )}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              fullWidth
              helperText="Previsión según el contador actual; el número definitivo lo asigna el servidor al guardar."
            />

            {/* Tipo de usuario */}
            <Autocomplete
              className="w-100 mr-5"
              id="autocomplete-type"
              filterSelectedOptions
              options={types}
              getOptionLabel={(option) => option?.titulo || ""}
              value={selectedType || null}
              onChange={(event, selected) => {
                setSelectedType(selected);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  label="Tipo de usuario"
                />
              )}
            />

            {/* Datos personales y contacto (comunes con registro web y asociados) */}
            <div style={{ marginTop: "20px", marginBottom: "20px" }}>
              <h5>Datos personales y contacto</h5>
              <hr />
            </div>

            <TextField
              id="dni"
              label="NIF / DNI"
              value={user.dni || ""}
              onChange={handleChange("dni")}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              required={user.asociado}
              fullWidth
            />

            <TextField
              id="telefono"
              label="Teléfono de contacto"
              value={user.telefono || ""}
              onChange={handleChange("telefono")}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              required={user.asociado}
              fullWidth
            />

            <TextField
              id="fechaNacimiento"
              label="Fecha de nacimiento"
              type="date"
              value={
                user.fechaNacimiento
                  ? new Date(user.fechaNacimiento).toISOString().split("T")[0]
                  : ""
              }
              onChange={handleDateChange("fechaNacimiento")}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              required={user.asociado}
              fullWidth
            />

            <div style={{ marginTop: "16px", marginBottom: "8px" }}>
              <label>Idioma</label>
              <Select
                className="my-2"
                value={selectedLanguage || ""}
                onChange={(selected) => setSelectedLanguage(selected)}
                options={languages}
                getOptionLabel={(option) => option.fullName}
                getOptionValue={(option) => option._id}
                placeholder="Seleccione un idioma..."
                isClearable
                styles={{
                  menu: (provided) => ({ ...provided, zIndex: 9999 }),
                }}
              />
            </div>

            {/* Domicilio (común con registro web y asociados) */}
            <div style={{ marginTop: "20px", marginBottom: "20px" }}>
              <h5>Domicilio</h5>
              <hr />
            </div>

            <div style={{ marginTop: "16px", marginBottom: "8px" }}>
              <label>País {user.asociado ? "*" : ""}</label>
              <Select
                className="my-2"
                value={selectedPais || ""}
                onChange={(selected) => {
                  setSelectedPais(selected);
                  setUser((prev) => ({
                    ...prev,
                    pais: selected?._id || "",
                    poblacion: "",
                    provincia: "",
                    cp: "",
                  }));

                  if (selected) {
                    setFieldsLocked({
                      cp: false,
                      poblacion: true,
                      provincia: true,
                    });
                  } else {
                    setFieldsLocked({
                      cp: true,
                      poblacion: true,
                      provincia: true,
                    });
                  }
                }}
                options={paises}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option._id}
                placeholder="Seleccione un país..."
                isClearable
                styles={{
                  menu: (provided) => ({ ...provided, zIndex: 9999 }),
                }}
              />
            </div>

            <TextField
              id="cp"
              label="Código Postal"
              value={user.cp || ""}
              onChange={(event) => {
                const value = event.target.value;
                setUser({ ...user, cp: value });

                if (selectedPais && value.length >= 4) {
                  getPopulationFromCP(value, selectedPais.code);
                } else {
                  getPopulationFromCP(value, selectedPais?.code, true);
                }
              }}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              fullWidth
              disabled={fieldsLocked.cp}
              helperText={
                fieldsLocked.cp
                  ? "Seleccione primero un país"
                  : isLoadingLocation
                  ? "Buscando población..."
                  : ""
              }
            />

            <TextField
              id="poblacion"
              label="Población"
              value={user.poblacion || ""}
              onChange={handleChange("poblacion")}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              fullWidth
              disabled={fieldsLocked.poblacion || isLoadingLocation}
              helperText={
                fieldsLocked.poblacion
                  ? "Se completará automáticamente con el CP"
                  : ""
              }
            />

            <TextField
              id="provincia"
              label="Provincia"
              value={user.provincia || ""}
              onChange={handleChange("provincia")}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              fullWidth
              disabled={fieldsLocked.provincia || isLoadingLocation}
              helperText={
                fieldsLocked.provincia
                  ? "Se completará automáticamente con el CP"
                  : ""
              }
            />

            <TextField
              id="via"
              label="Tipo de vía"
              select
              value={user.via || ""}
              onChange={handleChange("via")}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              fullWidth
            >
              {TIPOS_VIA.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              id="direccion"
              label="Dirección"
              value={user.direccion || ""}
              onChange={handleChange("direccion")}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              fullWidth
            />

            <TextField
              id="numero"
              label="Número"
              value={user.numero || ""}
              onChange={handleChange("numero")}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              fullWidth
            />

            <TextField
              id="piso"
              label="Piso"
              value={user.piso || ""}
              onChange={handleChange("piso")}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              fullWidth
            />

            <TextField
              id="puerta"
              label="Puerta"
              value={user.puerta || ""}
              onChange={handleChange("puerta")}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              fullWidth
            />

            {/* Gestión de contraseña */}
            {!userId || changePassword ? (
              <>
                <div style={{ marginTop: "20px", marginBottom: "10px" }}>
                  <h5>Contraseña {!userId ? "*" : ""}</h5>
                </div>

                <TextField
                  id="password"
                  label="Contraseña"
                  value={newPassword.password || ""}
                  onChange={(event) => {
                    if (event.target.value !== " ")
                      setNewPassword({
                        ...newPassword,
                        password: event.target.value,
                      });
                  }}
                  InputLabelProps={{ shrink: true }}
                  type="password"
                  margin="normal"
                  variant="outlined"
                  required
                  fullWidth
                />

                <TextField
                  id="repeatPassword"
                  label="Repetir contraseña"
                  value={newPassword.repeatPassword || ""}
                  onChange={(event) => {
                    if (event.target.value !== " ")
                      setNewPassword({
                        ...newPassword,
                        repeatPassword: event.target.value,
                      });
                  }}
                  InputLabelProps={{ shrink: true }}
                  type="password"
                  margin="normal"
                  variant="outlined"
                  required
                  fullWidth
                />

                {userId && (
                  <Button
                    onClick={() => {
                      setChangePassword(false);
                      setNewPassword({
                        password: null,
                        repeatPassword: null,
                      });
                    }}
                    variant="outlined"
                    style={{ marginTop: "10px" }}
                  >
                    Cancelar cambio de contraseña
                  </Button>
                )}
              </>
            ) : (
              <div style={{ marginTop: "20px" }}>
                <Button
                  onClick={() => setChangePassword(true)}
                  variant="outlined"
                  color="primary"
                  style={{ marginRight: "10px" }}
                >
                  Cambiar contraseña
                </Button>
                <Button
                  onClick={() => {
                    const newPassword = prompt(
                      "Introduce la nueva contraseña (mínimo 6 caracteres):",
                    );
                    if (newPassword && newPassword.length >= 6) {
                      resetUserPassword(userId, newPassword)
                        .then((res) => {
                          if (res.status === 200) {
                            alertSuccess({
                              title: "¡Contraseña restablecida!",
                              customMessage:
                                "La contraseña se ha restablecido correctamente.",
                            });
                          }
                        })
                        .catch((error) => {
                          alertError({
                            error: error,
                            customMessage:
                              "No se pudo restablecer la contraseña.",
                          });
                        });
                    } else if (newPassword) {
                      alertError({
                        error: null,
                        customMessage:
                          "La contraseña debe tener al menos 6 caracteres.",
                      });
                    }
                  }}
                  variant="outlined"
                  color="secondary"
                >
                  Restablecer contraseña
                </Button>
              </div>
            )}

            {/* Checkboxes */}
            <div style={{ marginTop: "20px" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={user.asociado || false}
                    onChange={() =>
                      setUser({ ...user, asociado: !user.asociado })
                    }
                    name="checkAsociado"
                  />
                }
                label="Asociado"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={user.jd || false}
                    onChange={() => setUser({ ...user, jd: !user.jd })}
                    name="checkJD"
                  />
                }
                label="J.D."
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={user.mailing || false}
                    onChange={() =>
                      setUser({ ...user, mailing: !user.mailing })
                    }
                    name="checkMailing"
                  />
                }
                label="Mailing"
              />

              {user.asociado && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={user.seram || false}
                      onChange={() => setUser({ ...user, seram: !user.seram })}
                      name="checkSeram"
                    />
                  }
                  label="SERAM"
                />
              )}
            </div>

            {/* Campos adicionales para socios */}
            {user.asociado && htmlSocios()}
          </CardBody>
        </Card>

        {/* Botones de acción */}
        <div style={{ marginTop: "20px" }}>
          {userId && (
            <>
              <ConfirmDialog
                title={"¿Estás seguro de que quieres eliminar este usuario?"}
                open={openConfirmDialog}
                setOpen={setOpenConfirmDialog}
                onConfirm={() => {
                  deleteUser(userId)
                    .then((res) => {
                      if (res.status === 204 || res.status === 200) {
                        alertSuccess({
                          title: "¡Eliminado!",
                          customMessage: "Usuario eliminado correctamente",
                        });
                        history.push("/users");
                      }
                    })
                    .catch((error) => {
                      alertError({
                        error: error,
                        customMessage: "No se pudo eliminar el usuario.",
                      });
                    });
                }}
              />
            </>
          )}
        </div>
      </>
    );
}
